import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle, CornersIn, CornersOut, Eye, Spinner, Users } from "@phosphor-icons/react";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CorrectAnswerDirection from "../components/CorrectAnswerDirection";

function Distribution({ items = [], alternatives = [], revealed = false, compact = false }) {
  const textByLetter = Object.fromEntries((alternatives || []).map((item) => [item.letter, item.text]));
  return (
    <div className="space-y-3">
      {items.map((item) => {
        const correct = revealed && alternatives.find((option) => option.letter === item.letter)?.is_correct;
        return (
          <div key={item.letter}>
            <div className={`flex items-center justify-between gap-4 ${compact ? "text-xs" : "text-lg"} mb-1`}>
              <span className="truncate"><b>{item.letter}</b> · {textByLetter[item.letter]}</span>
              <span className="font-mono-data shrink-0">{item.count} · {item.percentage}%</span>
            </div>
            <div className={`${compact ? "h-4" : "h-8"} border border-border bg-muted/20 overflow-hidden`}>
              <div className={`h-full transition-all duration-500 ${correct ? "bg-emerald-500" : "bg-primary"}`}
                style={{ width: `${Math.max(0, Math.min(100, item.percentage || 0))}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DisplayMedia({ code, id, compact = false }) {
  if (!id) return null;
  return <img src={`/api/live-display/${code}/media/${id}`} alt="Ilustração da questão"
    className={`${compact ? "max-h-32" : "max-h-[32vh]"} max-w-full object-contain mx-auto my-4`} />;
}

export default function LiveDisplayPage() {
  const { displayCode } = useParams();
  const navigate = useNavigate();
  const normalizedCode = String(displayCode || "").trim().toUpperCase();
  const [typedCode, setTypedCode] = useState("");
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(Boolean(normalizedCode));
  const [error, setError] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState("fit");
  const [fitScale, setFitScale] = useState(1);
  const polling = useRef(false);
  const stageRef = useRef(null);
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);

  const poll = useCallback(async () => {
    if (!normalizedCode || polling.current) return;
    polling.current = true;
    try {
      const response = await api.get(`/live-display/${normalizedCode}`);
      setState(response.data);
      setError("");
    } catch (requestError) {
      setError(fmtError(requestError));
    } finally {
      polling.current = false;
      setLoading(false);
    }
  }, [normalizedCode]);

  useEffect(() => {
    if (!normalizedCode) return undefined;
    poll();
    const timer = window.setInterval(poll, 800);
    return () => window.clearInterval(timer);
  }, [normalizedCode, poll]);

  useEffect(() => {
    const update = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);

  const recalculateFit = useCallback(() => {
    if (viewMode !== "fit") {
      setFitScale(1);
      return;
    }
    const viewport = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewport || !canvas) return;
    const availableWidth = Math.max(1, viewport.clientWidth);
    const availableHeight = Math.max(1, viewport.clientHeight);
    const contentWidth = Math.max(1, canvas.scrollWidth);
    const contentHeight = Math.max(1, canvas.scrollHeight);
    // "Ajustar à tela" must also enlarge a compact slide on a projector. The
    // old cap of 1 only allowed shrinking and left a large unused right area.
    const nextScale = Math.min(availableWidth / contentWidth, availableHeight / contentHeight);
    setFitScale((current) => Math.abs(current - nextScale) > 0.002 ? nextScale : current);
  }, [viewMode]);

  useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(recalculateFit);
    const observer = new ResizeObserver(recalculateFit);
    if (viewportRef.current) observer.observe(viewportRef.current);
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [recalculateFit, state?.phase, state?.current_position, state?.answer_revealed]);

  const submitCode = (event) => {
    event.preventDefault();
    const code = typedCode.replace(/[^a-z0-9]/gi, "").toUpperCase();
    if (code.length >= 6) navigate(`/apresentar/${code}`);
  };

  const toggleFullscreen = async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    else if (stageRef.current?.requestFullscreen) await stageRef.current.requestFullscreen();
  };

  if (!normalizedCode) {
    return (
      <main className="min-h-[calc(100vh-49px)] flex items-center justify-center bg-background px-6 py-12">
        <form onSubmit={submitCode} className="w-full max-w-xl border border-border bg-card p-8 md:p-12">
          <div className="font-display text-3xl font-black tracking-tighter">SAKI<span className="text-primary">/</span>X<span className="text-[0.7em]">m</span></div>
          <div className="label-eyebrow mt-10">projeção segura · sem login</div>
          <h1 className="font-display text-5xl font-black tracking-tighter mt-2">Apresentar prova.</h1>
          <p className="text-muted-foreground mt-4">
            Digite o código de projeção mostrado no celular do professor. Este computador apenas exibe a aula e não recebe acesso à conta.
          </p>
          <label className="block mt-8">
            <span className="label-eyebrow">código de projeção</span>
            <Input autoFocus value={typedCode} maxLength={8}
              onChange={(event) => setTypedCode(event.target.value.replace(/[^a-z0-9]/gi, "").toUpperCase())}
              className="rounded-none mt-2 h-16 text-center text-3xl font-mono-data tracking-[0.18em] uppercase"
              placeholder="AB12CD34" data-testid="display-code-input" />
          </label>
          <Button type="submit" disabled={typedCode.length < 6}
            className="rounded-none w-full mt-4 h-12 font-mono-data uppercase tracking-widest" data-testid="display-code-submit">
            Abrir apresentação
          </Button>
        </form>
      </main>
    );
  }

  if (loading) return <div className="min-h-screen grid place-items-center"><Spinner size={42} className="animate-spin text-primary" /></div>;
  if (error && !state) {
    return <main className="min-h-screen grid place-items-center px-6"><div className="max-w-lg border border-destructive bg-destructive/5 p-8 text-center"><h1 className="font-display text-3xl font-black">Apresentação não encontrada.</h1><p className="mt-3 text-muted-foreground">{error}</p><Button onClick={() => navigate("/apresentar")} className="rounded-none mt-6">Digitar outro código</Button></div></main>;
  }

  const question = state?.question;
  const alternatives = question?.alternatives || [];
  const fitView = viewMode === "fit";
  const panelHeight = fitView ? "h-full" : "min-h-[calc(100vh-105px)]";
  const questionTextClass = fitView ? "text-[clamp(1.15rem,2.25vh,2.15rem)] leading-[1.15]" : "text-3xl 2xl:text-5xl leading-relaxed";
  const optionTextClass = fitView ? "text-[clamp(.86rem,1.5vh,1.15rem)] leading-snug" : "text-xl 2xl:text-2xl";
  const contentPadding = fitView ? "p-[clamp(.9rem,2.2vh,2rem)]" : "p-8 xl:p-12";
  const questionGrid = fitView ? "grid-cols-[minmax(0,1fr)_minmax(250px,22vw)]" : "xl:grid-cols-[1fr_420px]";
  return (
    <main ref={stageRef} className={`${fitView ? "h-screen overflow-hidden" : "min-h-screen overflow-y-auto"} bg-background p-3 md:p-5 flex flex-col`}>
      <div className="flex items-center justify-between gap-4 mb-3 shrink-0">
        <div className="font-display text-2xl font-black">SAKI<span className="text-primary">/</span>Xm · apresentação</div>
        <div className="flex items-center gap-2">
          <div className="flex border border-border" role="group" aria-label="Tamanho da apresentação">
            <Button onClick={() => setViewMode("original")} variant={fitView ? "ghost" : "default"}
              className="rounded-none border-0" aria-pressed={!fitView} data-testid="display-original-size">
              Tamanho original
            </Button>
            <Button onClick={() => setViewMode("fit")} variant={fitView ? "default" : "ghost"}
              className="rounded-none border-0 border-l border-border" aria-pressed={fitView} data-testid="display-fit-screen">
              Ajustar à tela
            </Button>
          </div>
          <Button onClick={toggleFullscreen} variant="outline" className="rounded-none">
            {isFullscreen ? <CornersIn className="mr-2" /> : <CornersOut className="mr-2" />}
            {isFullscreen ? "Sair da tela inteira" : "Tela inteira"}
          </Button>
        </div>
      </div>

      <div ref={viewportRef} className={`${fitView ? "flex-1 min-h-0 overflow-hidden" : "overflow-visible"}`}>
      <div ref={canvasRef} className={fitView ? "w-full min-w-0 origin-top-left" : ""}
        style={fitView ? { transform: `scale(${fitScale})` } : undefined} data-view-mode={viewMode}>
      {state?.phase === "lobby" && (
        <section className={`${panelHeight} w-full grid ${fitView ? "grid-cols-[minmax(0,1fr)_minmax(250px,30vw)]" : "lg:grid-cols-[1fr_42vw]"} border border-border bg-card`}>
          <div className={`${fitView ? "p-[clamp(1.25rem,4vh,4rem)]" : "p-10 xl:p-16"} flex flex-col justify-center`}>
            <div className="label-eyebrow">aguardando o professor iniciar</div>
            <h1 className="font-display font-black text-5xl xl:text-7xl mt-4">{state.exam_name}</h1>
            <div className="mt-12 text-xl text-muted-foreground">Alunos entram com o código</div>
            <div className="font-mono-data text-7xl xl:text-8xl font-black tracking-[0.14em] text-primary mt-3">{state.join_code}</div>
            <div className="mt-10 flex items-center gap-3 text-3xl"><Users weight="duotone" /> {state.participant_count} participante(s)</div>
          </div>
          <div className={`${fitView ? "p-[clamp(1rem,3vh,3rem)]" : "p-10"} bg-white flex flex-col items-center justify-center border-l border-border`}>
            <img src={state.qr_code_data_url} alt="QR para os alunos" className="w-full max-w-[520px] aspect-square" />
            <div className="font-mono-data text-lg break-all text-center mt-5">{state.join_url}</div>
          </div>
        </section>
      )}

      {state?.phase !== "lobby" && state?.session_mode === "online_exam" && (
        <section className={`${panelHeight} w-full grid place-items-center border border-border bg-card`}>
          <div className="max-w-4xl text-center p-10">
            <div className="label-eyebrow">prova online individual</div>
            <h1 className="font-display text-6xl font-black mt-4">{state.exam_name}</h1>
            <p className="text-xl text-muted-foreground mt-6">A prova está {state.phase === "finished" ? "encerrada" : "em andamento"}. Questões e resultados individuais não são exibidos nesta tela pública.</p>
            <div className="grid md:grid-cols-2 gap-6 mt-10 items-center">
              <img src={state.qr_code_data_url} alt="QR para os alunos" className="w-full max-w-[360px] mx-auto" />
              <div><div className="text-xl text-muted-foreground">Código de acesso</div><div className="font-mono-data text-7xl font-black tracking-[0.12em] text-primary mt-3">{state.join_code}</div><div className="mt-8 text-3xl"><Users className="inline mr-3" />{state.participant_count} participante(s)</div></div>
            </div>
          </div>
        </section>
      )}

      {state?.phase !== "lobby" && state?.session_mode !== "online_exam" && (
        <section className={`${panelHeight} w-full grid ${questionGrid} border border-border bg-card`}>
          <div className="flex flex-col min-w-0">
            <div className={`border-b border-border ${fitView ? "px-4 py-3" : "px-8 py-5"} flex justify-between gap-4`}>
              <div><div className="label-eyebrow">{state.exam_name}</div><div className="font-mono-data text-xl mt-1">Questão {state.current_position} de {state.total_questions}</div></div>
              <div className={`px-4 py-2 border font-mono-data text-xs uppercase tracking-widest self-center ${state.answer_revealed ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-primary text-primary"}`}>
                {state.answer_revealed ? "resposta revelada" : "recebendo respostas"}
              </div>
            </div>
            {question && <div className={`${contentPadding} flex-1 min-h-0`}>
              <div className={`text-primary font-mono-data ${fitView ? "text-[11px] mb-2" : "mb-4"}`}>{question.knowledge_area} · {question.system_context}</div>
              <h2 className={`${questionTextClass} font-semibold`}>{question.stem}</h2>
              {(question.stem_media_ids || []).map((id) => <DisplayMedia key={id} id={id} code={normalizedCode} />)}
              <div className={`grid md:grid-cols-2 ${fitView ? "gap-2 mt-4" : "gap-4 mt-9"}`}>
                {alternatives.map((option) => {
                  const correct = state.answer_revealed && option.is_correct;
                  return <div key={option.letter} className={`border ${fitView ? "p-3" : "p-5"} ${optionTextClass} ${correct ? "border-emerald-600 bg-emerald-50" : "border-border"}`}><b className={correct ? "text-emerald-700" : "text-primary"}>{option.letter}</b> · {option.text}{option.media_id && <DisplayMedia code={normalizedCode} id={option.media_id} compact />}{correct && <CheckCircle className="inline ml-3 text-emerald-700" weight="fill" />}</div>;
                })}
              </div>
              {state.answer_revealed && <CorrectAnswerDirection alternatives={alternatives} className={fitView ? "mt-4 text-sm" : "mt-8 text-lg"} />}
              {state.answer_revealed && question.explanation && <div className={`border-l-4 border-emerald-600 bg-emerald-50 ${fitView ? "mt-3 p-3 text-sm" : "mt-4 p-5 text-lg"}`}><b>Comentário:</b> {question.explanation}</div>}
            </div>}
          </div>
          <aside className={`border-l border-border ${fitView ? "p-4" : "p-6"} bg-background/40 overflow-hidden`}>
            <div className={`font-display ${fitView ? "text-4xl" : "text-5xl"} font-black`}>{state.response_count}<span className={`${fitView ? "text-lg" : "text-2xl"} text-muted-foreground`}>/{state.participant_count}</span></div>
            <div className={`label-eyebrow mt-2 ${fitView ? "mb-4" : "mb-8"}`}>respostas recebidas</div>
            {state.distribution_visible ? <Distribution items={state.distribution} alternatives={alternatives} revealed={state.answer_revealed} compact={fitView} /> : <div className="border border-border bg-card p-5 text-sm text-muted-foreground"><Eye className="mb-3 text-primary" size={26} /><b className="block text-foreground">Resultados ocultos pelo professor</b><span className="block mt-2">{state.result_display_mode === "after_reveal" ? "A distribuição aparecerá após a resposta ser revelada." : "Esta aplicação não exibirá a distribuição das respostas na projeção."}</span></div>}
            {state.phase === "finished" && <div className={`${fitView ? "mt-4 p-3" : "mt-8 p-5"} border border-emerald-600 bg-emerald-50`}><CheckCircle size={fitView ? 22 : 30} weight="fill" className="text-emerald-700" /><b className="block mt-3">Aula encerrada</b><span className="text-sm">Os resultados foram registrados pelo professor.</span></div>}
          </aside>
        </section>
      )}
      </div>
      </div>
    </main>
  );
}
